// Owner: slides-editor (plan §9.10, §4.2). Keyboard commands of the deck editor, discovered by glob
// (`features/**/commands.ts`) so the `?` sheet and the conflict matrix know them. Three scopes:
//
// - `slides.editor` (focus anywhere in the editor): insert tools on single keys (V T R O L ⇧L I C K D),
//   ⌘↵ new slide, ⌘⇧D duplicate slide, ⌘⌥M comment, ⌘S save now, ⌥⌘P present;
// - `slides.rail` (focus in the slides rail): ↑ ↓ Home End move through slides, ⌘↑ ⌘↓ move them,
//   ⌘D duplicates, Delete deletes (with Undo), ⌘A selects every slide, ↵ goes to the slide;
// - `slides.text` (while a text box is edited): ⌘Z / ⌘⇧Z on the deck's history, so one undo removes
//   a typed word and history survives saves.
//
// The canvas's own verbs (selection, nudges, grouping, order, zoom …) are canvas-kit's `canvas.*`.
// Keep this file tiny: run functions call into the scope's context.
import { defineCommand, defineScope } from '@dataroom/interaction';
import { commentCommand } from '../../collab/commands';
import { saveNowCommand } from '../../editor-kit/commands';
import { defineAppCommands } from '../../extensions/commands';
import { t } from '../../lib/i18n';

export type InsertTool =
  | 'text'
  | 'rect'
  | 'ellipse'
  | 'line'
  | 'arrow'
  | 'image'
  | 'chart'
  | 'code'
  | 'table'
  | 'metric'
  | 'icon'
  | 'triangle'
  | 'star'
  | 'polygon'
  /** Controlled HTML and library components on a slide (F24). */
  | 'markup'
  | 'component';

export type DeckEditorContext = {
  canEdit: () => boolean;
  /** Arm a tool (click-drag draws, a click places) or the select tool (`null`). */
  tool: (tool: InsertTool | null) => void;
  newSlide: () => void;
  duplicateSlide: () => void;
  comment: () => boolean | void;
  saveNow: () => Promise<boolean>;
  present: () => void;
};

export type DeckRailContext = {
  step: (delta: number, extend: boolean) => void;
  edge: (end: 'first' | 'last') => void;
  move: (delta: -1 | 1) => boolean;
  canMove: (delta: -1 | 1) => boolean;
  duplicate: () => void;
  remove: () => void;
  selectAll: () => void;
  open: () => void;
  canEdit: () => boolean;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
};

export type DeckTextContext = {
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
};

export const deckScope = defineScope<DeckEditorContext>({
  id: 'slides.editor',
  kind: 'editor',
  title: () => t('slides.scope.editor'),
});

export const deckRailScope = defineScope<DeckRailContext>({
  id: 'slides.rail',
  kind: 'editor',
  title: () => t('slides.scope.rail'),
});

export const deckTextScope = defineScope<DeckTextContext>({
  id: 'slides.text',
  kind: 'editor',
  title: () => t('slides.scope.text'),
});

const TOOL_KEYS: Array<[InsertTool | null, string, string]> = [
  [null, 'v', 'slides.tool.select'],
  ['text', 't', 'slides.tool.text'],
  ['rect', 'r', 'slides.tool.rect'],
  ['ellipse', 'o', 'slides.tool.ellipse'],
  ['line', 'l', 'slides.tool.line'],
  ['arrow', 'shift+l', 'slides.tool.arrow'],
  ['image', 'i', 'slides.tool.image'],
  ['chart', 'c', 'slides.tool.chart'],
  ['code', 'k', 'slides.tool.code'],
  ['table', 'd', 'slides.tool.table'],
];

const insert = () => t('slides.group.insert');
const slidesGroup = () => t('slides.group.slides');

export default defineAppCommands({
  id: 'slides',
  loadTitles: () => import('../../messages-ready/slides'),
  scopes: [deckScope, deckRailScope, deckTextScope],
  commands: [
    ...TOOL_KEYS.map(([tool, key, title]) =>
      defineCommand<DeckEditorContext>({
        id: `slides.tool.${tool ?? 'select'}`,
        title: () => t(title as 'slides.tool.select'),
        shortcut: key,
        scope: deckScope,
        group: insert,
        // `C` creates records elsewhere; in a deck it is the chart tool.
        ...(key === 'c' ? { overrides: ['app.create'] } : {}),
        when: (ctx) => tool === null || ctx.canEdit(),
        run: (ctx) => ctx.tool(tool),
      }),
    ),
    defineCommand<DeckEditorContext>({
      id: 'slides.newSlide',
      title: () => t('slides.cmd.newSlide'),
      icon: 'plus',
      shortcut: 'mod+enter',
      scope: deckScope,
      group: slidesGroup,
      when: (ctx) => ctx.canEdit(),
      run: (ctx) => ctx.newSlide(),
    }),
    defineCommand<DeckEditorContext>({
      id: 'slides.duplicateSlide',
      title: () => t('slides.cmd.duplicateSlide'),
      icon: 'copy',
      shortcut: 'mod+shift+d',
      scope: deckScope,
      group: slidesGroup,
      when: (ctx) => ctx.canEdit(),
      run: (ctx) => ctx.duplicateSlide(),
    }),
    defineCommand<DeckEditorContext>({
      id: 'slides.present',
      title: () => t('slides.cmd.present'),
      icon: 'play',
      shortcut: 'mod+alt+p',
      scope: deckScope,
      textSafe: true,
      run: (ctx) => ctx.present(),
    }),
    commentCommand(deckScope),
    saveNowCommand(deckScope),
    // The rail.
    defineCommand<DeckRailContext>({
      id: 'slides.rail.next',
      title: () => t('slides.cmd.nextSlide'),
      shortcut: 'arrowdown',
      scope: deckRailScope,
      repeat: true,
      run: (ctx) => ctx.step(1, false),
    }),
    defineCommand<DeckRailContext>({
      id: 'slides.rail.previous',
      title: () => t('slides.cmd.previousSlide'),
      shortcut: 'arrowup',
      scope: deckRailScope,
      repeat: true,
      run: (ctx) => ctx.step(-1, false),
    }),
    defineCommand<DeckRailContext>({
      id: 'slides.rail.extendNext',
      title: () => t('slides.cmd.extendNext'),
      shortcut: 'shift+arrowdown',
      scope: deckRailScope,
      repeat: true,
      hidden: true,
      run: (ctx) => ctx.step(1, true),
    }),
    defineCommand<DeckRailContext>({
      id: 'slides.rail.extendPrevious',
      title: () => t('slides.cmd.extendPrevious'),
      shortcut: 'shift+arrowup',
      scope: deckRailScope,
      repeat: true,
      hidden: true,
      run: (ctx) => ctx.step(-1, true),
    }),
    defineCommand<DeckRailContext>({
      id: 'slides.rail.first',
      title: () => t('slides.cmd.firstSlide'),
      shortcut: 'home',
      scope: deckRailScope,
      run: (ctx) => ctx.edge('first'),
    }),
    defineCommand<DeckRailContext>({
      id: 'slides.rail.last',
      title: () => t('slides.cmd.lastSlide'),
      shortcut: 'end',
      scope: deckRailScope,
      run: (ctx) => ctx.edge('last'),
    }),
    defineCommand<DeckRailContext>({
      id: 'slides.rail.moveUp',
      title: () => t('slides.cmd.moveUp'),
      shortcut: 'mod+arrowup',
      scope: deckRailScope,
      repeat: true,
      when: (ctx) => ctx.canEdit() && ctx.canMove(-1),
      run: (ctx) => ctx.move(-1),
    }),
    defineCommand<DeckRailContext>({
      id: 'slides.rail.moveDown',
      title: () => t('slides.cmd.moveDown'),
      shortcut: 'mod+arrowdown',
      scope: deckRailScope,
      repeat: true,
      when: (ctx) => ctx.canEdit() && ctx.canMove(1),
      run: (ctx) => ctx.move(1),
    }),
    defineCommand<DeckRailContext>({
      id: 'slides.rail.duplicate',
      title: () => t('slides.cmd.duplicate'),
      shortcut: 'mod+d',
      scope: deckRailScope,
      when: (ctx) => ctx.canEdit(),
      run: (ctx) => ctx.duplicate(),
    }),
    defineCommand<DeckRailContext>({
      id: 'slides.rail.delete',
      title: () => t('slides.cmd.delete'),
      shortcut: ['delete', 'backspace'],
      scope: deckRailScope,
      when: (ctx) => ctx.canEdit(),
      run: (ctx) => ctx.remove(),
    }),
    defineCommand<DeckRailContext>({
      id: 'slides.rail.selectAll',
      title: () => t('slides.cmd.selectAll'),
      shortcut: 'mod+a',
      scope: deckRailScope,
      run: (ctx) => ctx.selectAll(),
    }),
    defineCommand<DeckRailContext>({
      id: 'slides.rail.open',
      title: () => t('slides.cmd.openSlide'),
      shortcut: 'enter',
      scope: deckRailScope,
      run: (ctx) => ctx.open(),
    }),
    defineCommand<DeckRailContext>({
      id: 'slides.rail.undo',
      title: () => t('slides.cmd.undo'),
      shortcut: 'mod+z',
      scope: deckRailScope,
      repeat: true,
      when: (ctx) => ctx.canUndo(),
      run: (ctx) => ctx.undo(),
    }),
    defineCommand<DeckRailContext>({
      id: 'slides.rail.redo',
      title: () => t('slides.cmd.redo'),
      shortcut: ['mod+shift+z', { windows: 'ctrl+y' }],
      scope: deckRailScope,
      repeat: true,
      when: (ctx) => ctx.canRedo(),
      run: (ctx) => ctx.redo(),
    }),
    // Text editing: the deck's history (one step per typing burst, kept across saves).
    defineCommand<DeckTextContext>({
      id: 'slides.text.undo',
      title: () => t('slides.cmd.undo'),
      shortcut: 'mod+z',
      scope: deckTextScope,
      textSafe: true,
      repeat: true,
      overrides: ['canvas.undo'],
      when: (ctx) => ctx.canUndo(),
      run: (ctx) => ctx.undo(),
    }),
    defineCommand<DeckTextContext>({
      id: 'slides.text.redo',
      title: () => t('slides.cmd.redo'),
      shortcut: ['mod+shift+z', { windows: 'ctrl+y' }],
      scope: deckTextScope,
      textSafe: true,
      repeat: true,
      overrides: ['canvas.redo'],
      when: (ctx) => ctx.canRedo(),
      run: (ctx) => ctx.redo(),
    }),
  ],
});
